import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import useEmblaCarousel from "embla-carousel-react";
import Autoplay from "embla-carousel-autoplay";
import { CaretLeft, CaretRight, MapPin, CalendarBlank, Star } from "@phosphor-icons/react";

const fmtDate = (iso) => {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
  } catch { return iso; }
};

export default function FeaturedCarousel({ events = [] }) {
  const [emblaRef, emblaApi] = useEmblaCarousel(
    { loop: true, align: "start", containScroll: "trimSnaps" },
    [Autoplay({ delay: 4000, stopOnInteraction: true })]
  );
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setCanScrollPrev(emblaApi.canScrollPrev());
    setCanScrollNext(emblaApi.canScrollNext());
  }, [emblaApi]);

  const scrollPrev = useCallback(() => emblaApi?.scrollPrev(), [emblaApi]);
  const scrollNext = useCallback(() => emblaApi?.scrollNext(), [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    onSelect();
    emblaApi.on("select", onSelect);
    emblaApi.on("reInit", onSelect);
    return () => { emblaApi.off("select", onSelect); };
  }, [emblaApi, onSelect]);

  if (events.length === 0) return null;

  return (
    <div className="embla relative">
      <div className="embla__viewport" ref={emblaRef}>
        <div className="embla__container">
          {events.map((event) => {
            const min = event.min_price ?? (event.ticket_types?.[0]?.price ?? 0);
            return (
              <div key={event.id} className="embla__slide">
                <Link
                  to={`/events/${event.id}`}
                  className="embla__slide__inner block group"
                >
                  <img
                    src={event.image_url}
                    alt={event.title}
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />

                  <div className="absolute top-3 left-4 flex items-center gap-1.5 bg-white/90 backdrop-blur-sm text-black text-[10px] tracking-widest font-bold uppercase px-2 py-0.5 rounded-full">
                    <Star size={10} weight="fill" className="text-[#4F46E5]" />
                    Featured
                  </div>

                  <div className="absolute bottom-3 left-4 right-4 text-white">
                    <h3 className="font-display text-lg leading-tight tracking-tight line-clamp-1">
                      {event.title}
                    </h3>
                    <div className="flex items-center gap-3 mt-1 text-xs text-white/80">
                      <span className="flex items-center gap-1">
                        <CalendarBlank size={12} weight="bold" />
                        {fmtDate(event.date)}
                      </span>
                      <span className="flex items-center gap-1">
                        <MapPin size={12} weight="bold" />
                        {event.city}
                      </span>
                    </div>
                    <div className="flex items-end justify-between mt-2">
                      <span className="font-display text-xl">৳{min.toLocaleString()}</span>
                      <span className="text-[10px] uppercase tracking-widest font-bold text-white/70 group-hover:text-white transition-colors">
                        Get Tickets →
                      </span>
                    </div>
                  </div>
                </Link>
              </div>
            );
          })}
        </div>
      </div>

      <div className="embla__controls mt-6">
        <button
          onClick={scrollPrev}
          disabled={!canScrollPrev}
          className="embla__button"
          aria-label="Previous"
        >
          <CaretLeft size={20} weight="bold" className="embla__button__svg" />
        </button>
        <button
          onClick={scrollNext}
          disabled={!canScrollNext}
          className="embla__button"
          aria-label="Next"
        >
          <CaretRight size={20} weight="bold" className="embla__button__svg" />
        </button>
      </div>
    </div>
  );
}
